<script lang="ts">
    import { router } from '@inertiajs/svelte';
    import type { Snippet } from 'svelte';

    interface PaginatorLink {
        url: string | null;
        label: string;
        active: boolean;
    }

    export interface Paginator<T = any> {
        data: T[];
        links: PaginatorLink[];
        current_page: number;
        last_page: number;
        total: number;
        from: number | null;
        to: number | null;
    }

    export interface Column {
        key: string | null;
        label: string;
        grow?: boolean;
        shrink?: boolean;
        align?: 'left' | 'right';
    }

    interface Props {
        columns: Column[];
        items: any[] | Paginator;
        sort?: string;
        direction?: 'asc' | 'desc';
        onsort?: (key: string, direction: 'asc' | 'desc') => void;
        onrowclick?: (item: any) => void;
        row: Snippet<[item: any, index: number]>;
        empty?: string;
        totalCount?: number | null;
        onclearfilters?: () => void;
        footerLeft?: Snippet;
    }

    let {
        columns,
        items,
        sort = '',
        direction = 'desc',
        onsort,
        onrowclick,
        row,
        empty = 'No items found.',
        totalCount = null,
        onclearfilters,
        footerLeft,
    }: Props = $props();

    const isPaginated = $derived(items && typeof items === 'object' && 'data' in items && 'links' in items);
    const paginator = $derived(isPaginated ? items as Paginator : null);
    const rows = $derived(isPaginated ? (items as Paginator).data : items as any[]);
    const visibleTotal = $derived(paginator ? paginator.total : rows.length);
    const hiddenCount = $derived(totalCount !== null ? totalCount - visibleTotal : 0);

    function handleSort(key: string) {
        if (!onsort) return;
        const newDirection = sort === key && direction === 'desc' ? 'asc' : 'desc';
        onsort(key, newDirection);
    }

    let scrollContainer = $state<HTMLDivElement | null>(null);
    let canScrollRight = $state(false);
    let canScrollLeft = $state(false);

    function checkScroll() {
        if (!scrollContainer) return;
        canScrollRight = scrollContainer.scrollLeft + scrollContainer.clientWidth < scrollContainer.scrollWidth - 2;
        canScrollLeft = scrollContainer.scrollLeft > 2;
    }

    $effect(() => {
        if (!scrollContainer) return;
        checkScroll();
        const observer = new ResizeObserver(checkScroll);
        observer.observe(scrollContainer);
        return () => observer.disconnect();
    });
</script>

<div class="bg-gray-900 rounded-lg shadow border border-gray-700 overflow-hidden">
    {#if rows.length === 0 && !paginator}
        <div class="p-6 text-sm text-gray-500">
            <p>{empty}</p>
            {#if onclearfilters && hiddenCount > 0}
                <div class="flex items-center gap-2 mt-2">
                    <span>{hiddenCount} {hiddenCount === 1 ? 'item' : 'items'} hidden by filters.</span>
                    <button
                        onclick={onclearfilters}
                        class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-sm font-medium bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 transition-colors"
                    >
                        <svg class="size-2.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>
                        Clear filters
                    </button>
                </div>
            {/if}
        </div>
    {:else}
        <div class="relative">
        {#if canScrollLeft}
            <div class="absolute inset-y-0 left-0 w-8 pointer-events-none z-10 bg-gradient-to-r from-gray-900 to-transparent"></div>
        {/if}
        {#if canScrollRight}
            <div class="absolute inset-y-0 right-0 w-8 pointer-events-none z-10 bg-gradient-to-l from-gray-900 to-transparent"></div>
        {/if}
        <div class="overflow-x-auto" bind:this={scrollContainer} onscroll={checkScroll}>
        <table class="min-w-full divide-y divide-gray-800">
            <thead class="bg-gray-950">
                <tr>
                    {#each columns as col (col.label)}
                        <th
                            class="px-6 py-3 text-xs font-medium text-gray-500 uppercase whitespace-nowrap {col.align === 'right' ? 'text-right' : 'text-left'} {col.key && onsort ? 'cursor-pointer select-none hover:text-gray-300' : ''} {col.grow ? 'w-full' : ''} {col.shrink ? 'w-px pr-2' : ''}"
                            onclick={() => col.key && onsort && handleSort(col.key)}
                        >
                            <span class="inline-flex items-center gap-1">
                                {col.label}
                                {#if col.key && onsort}
                                    {#if col.key === sort}
                                        <span class="text-gray-300">{direction === 'asc' ? '▲' : '▼'}</span>
                                    {:else}
                                        <span class="text-gray-300">▲</span>
                                    {/if}
                                {/if}
                            </span>
                        </th>
                    {/each}
                </tr>
            </thead>
            <tbody class="divide-y divide-gray-800">
                {#each rows as item, i (i)}
                    <tr
                        onclick={onrowclick ? () => onrowclick(item) : undefined}
                        class={onrowclick ? 'cursor-pointer hover:bg-gray-800 transition-colors' : ''}
                    >
                        {@render row(item, i)}
                    </tr>
                {/each}
            </tbody>
        </table>
        </div>
        </div>
    {/if}

    {#if paginator || footerLeft || (onclearfilters && hiddenCount > 0 && rows.length > 0)}
        <div class="px-6 py-3 border-t border-gray-800 bg-gray-950 flex items-center justify-between gap-4">
            <div class="flex items-center gap-3 text-sm text-gray-500">
                {#if paginator}
                    {#if paginator.from && paginator.to}
                        <span>Showing {paginator.from}–{paginator.to} of {paginator.total}</span>
                    {:else}
                        <span>No items found</span>
                    {/if}
                {/if}
                {#if onclearfilters && hiddenCount > 0}
                    {#if paginator}<span class="text-gray-600">·</span>{/if}
                    <span>{hiddenCount} hidden by filters</span>
                    <button
                        onclick={onclearfilters}
                        class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-sm font-medium bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 transition-colors"
                    >
                        <svg class="size-2.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>
                        Clear filters
                    </button>
                {/if}
                {#if footerLeft}
                    {@render footerLeft()}
                {/if}
            </div>

            {#if paginator && paginator.last_page > 1}
                {@const pageUrl = (p: number) => {
                    const base = paginator.links.find(l => l.url)?.url ?? '';
                    return base.replace(/[?&]page=\d+/, (m) => m[0] + 'page=' + p);
                }}
                {@const btnClass = (active: boolean, disabled: boolean) =>
                    disabled
                        ? 'px-2.5 py-1 rounded text-sm font-medium bg-gray-800/50 text-gray-700 cursor-default'
                        : active
                            ? 'px-2.5 py-1 rounded text-sm font-medium bg-cyan-600 text-white'
                            : 'px-2.5 py-1 rounded text-sm font-medium bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 transition-colors'
                }
                <nav class="flex items-center gap-1">
                    <button
                        onclick={() => router.visit(pageUrl(1), { preserveState: true })}
                        disabled={paginator.current_page === 1}
                        class={btnClass(false, paginator.current_page === 1)}
                        title="First page"
                    >
                        &laquo;
                    </button>
                    {#each paginator.links as link (link.label)}
                        {#if link.url}
                            <button
                                onclick={() => router.visit(link.url!, { preserveState: true })}
                                class={btnClass(link.active, false)}
                                style={link.active ? 'view-transition-name: active-page' : ''}
                            >
                                {link.label.includes('Previous') ? '‹' : link.label.includes('Next') ? '›' : link.label}
                            </button>
                        {:else if link.label.includes('Previous') || link.label.includes('Next')}
                            <span class={btnClass(false, true)}>
                                {link.label.includes('Previous') ? '‹' : '›'}
                            </span>
                        {:else}
                            <span class="px-1 py-1 text-sm text-gray-600">
                                <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                                {@html link.label}
                            </span>
                        {/if}
                    {/each}
                    <button
                        onclick={() => router.visit(pageUrl(paginator.last_page), { preserveState: true })}
                        disabled={paginator.current_page === paginator.last_page}
                        class={btnClass(false, paginator.current_page === paginator.last_page)}
                        title="Last page"
                    >
                        &raquo;
                    </button>
                </nav>
            {/if}
        </div>
    {/if}
</div>
